/**
 * @deprecated Last used in app version 5.5.0. Use ExperimentalPrice instead.
 */
export class Price {
  static ofCents(cents, currency) {
    return Object.assign(new Price(), {
      currency,
      cents
    });
  }
  // please keep these default initializers until we upgrade NCApp3 and NCWeb3 to typescript 2.7+ and can use "!definitive assignment"
  /**
   * @description
   * The currency code e.g USD
   */
  currency = '';
  /**
   * @description
   * The amount in cents. E.g 100[USD] => 10000
   */
  cents = 0;
  /**
   * @description
   * The discount in percentage. E.g 100[USD]-25% => 25
   */
  discount = 0;
  /**
   * @description
   * Returns the payable amount as a percentage e.g 100[USD]-25% => .75
   */
  get payable() {
    return 1 - this.discount / 100;
  }
  /**
   * @description
   * Returns the amount, with discount applied e.g 100[USD]-25% => 75.00
   */
  get amount() {
    const amount = this.cents * this.payable / 100;
    return Number.parseFloat(amount.toFixed(2));
  }
  /**
   * @description
   * Returns the amount without discount e.g 100[USD]-25% => 100.00
   */
  get amountWithoutDiscount() {
    return this.cents / 100;
  }
  /**
   * @param raw 100[USD] or with discount 100[USD]-25%
   */
  constructor(raw) {
    if (raw) {
      const parsed = raw.match(PRICE_VALIDATION_REGEX);
      if (parsed) {
        this.cents = Math.round(Number.parseFloat(parsed[1]) * 100);
        this.currency = parsed[3];
        this.discount = Number.parseFloat(parsed[5]) || 0;
      }
    }
  }
  match(matcher) {
    return this.toString().match(matcher);
  }
  toString() {
    return `${this.amountWithoutDiscount}[${this.currency}]${this.discount ? '-' + this.discount + '%' : ''}`;
  }
  toJSON() {
    return this.toString();
  }
  toLocaleString(locale) {
    const hasFraction = !!(this.amount % 1);
    const locales = (typeof locale === 'string' ? [locale] : locale).map(l => l.replace(/\-\w+$/, ''));
    const formattedString = this.amount.toLocaleString(locales, {
      style: 'currency',
      currency: this.currency,
      minimumFractionDigits: hasFraction ? 2 : 0,
      maximumFractionDigits: hasFraction ? 2 : 0
    });
    const fallbackString = this.toString()
    // eslint-disable-next-line unicorn/prefer-string-replace-all
    .replace(/(\[|])/g, ' ').trim();
    if (/^[0-9,.]+$/.test(formattedString)) {
      return fallbackString;
    }
    return formattedString;
  }
  multiply(quantity) {
    this.cents = Math.round(this.cents * quantity);
    return this;
  }
  // non-mutating!
  divide(divider) {
    return Price.ofCents(Math.round(this.cents / divider), this.currency);
  }
  add(price) {
    this.cents = Math.round(this.cents + price.cents * price.payable);
    return this;
  }
  /**
   * Divides the price by the duration in months
   * NOTE: we're using Math.floor instead of Math.round to make sure that we're always displaying the lowest price
   * which is desirable for marketing purposes
   * @example With Math.floor we get 59.99[USD] / 12 = 4.999166666666667[USD] => 4.99[USD], whereas Math.round would return 5.00[USD]
   * @param duration in months
   * @returns the monthly price
   */
  perMonth(duration) {
    const monthlyPrice = Price.ofCents(Math.floor(this.cents / duration), this.currency);
    if (this.discount) {
      return new Price(`${monthlyPrice.amount}[${monthlyPrice.currency}]-${this.discount}%`);
    }
    const hasFraction = !!(this.amount % 1);
    if (monthlyPrice && !hasFraction) {
      monthlyPrice.cents = Math.round(monthlyPrice.amount) * 100;
    }
    return monthlyPrice;
  }
}
const PRICE_VALIDATION_REGEX = /^(-?\d+(\.\d+)?)\[([A-Z]{3})](-([\d.]+)%)?$/;